import { Body1, makeStyles, Subtitle2, tokens } from '@fluentui/react-components'
import { DocumentAdd48Color } from '@fluentui/react-icons'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUploadFiles } from '../../hooks'

const useStyles = makeStyles({
  container: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  overlay: {
    position: 'fixed',
    left: '0',
    top: '0',
    right: '0',
    bottom: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.01)',
    backdropFilter: 'blur(30px)',
    zIndex: 1000,
    pointerEvents: 'none',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    textAlign: 'center',
  },
  subtitle: {
    margin: '0',
    padding: '0',
    lineHeight: tokens.lineHeightBase200,
  },
  body: {
    padding: '0',
    lineHeight: tokens.lineHeightBase300,
  },
  icon: {
    paddingBottom: tokens.spacingHorizontalS,
  },
})

type DropzoneProps = {
  children: React.ReactNode
}

const Dropzone = ({ children }: DropzoneProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [isDragActive, setIsDragActive] = useState(false)
  const { uploadFiles } = useUploadFiles()

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      setIsDragActive(false)
      const files = e.dataTransfer.files

      if (!files || files.length === 0) return

      uploadFiles(files)
    },
    [uploadFiles]
  )

  return (
    <div
      className={classes.container}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {isDragActive && (
        <div className={classes.overlay}>
          <div className={classes.content}>
            <DocumentAdd48Color className={classes.icon} />
            <Subtitle2 as="h2" className={classes.subtitle}>
              {t('dropzone.addFile')}
            </Subtitle2>
            <Body1 as="p" className={classes.body}>
              {t('dropzone.dropInstruction')}
            </Body1>
          </div>
        </div>
      )}
      {children}
    </div>
  )
}

export { Dropzone }
